import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { X, CheckCircle2, Loader2, ArrowRight, Clock, Mail } from "lucide-react";
import { toast } from "sonner";
import { formatPhoneNumberIntl } from "react-phone-number-input";
import { isValidPhoneNumber } from "@/components/PhoneField";
import DatePicker from "@/components/DatePicker";
import TimeSlotPicker from "@/components/TimeSlotPicker";
import Dropdown from "@/components/Dropdown";
import { BOOKING_MODAL } from "@/constants/testIds";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Corporate's "Training Category" labels -- used only to turn whatever
// raw value prefilled into data.courseCategory into a nicer label for
// the booking payload's course_category (see handleSubmit); falls back
// to the raw value itself when it doesn't match one of these keys, which
// is the common case since the prefilled value is usually the enquiry
// form's own Training Category wording, not this catalog vocabulary.
const CORPORATE_CATEGORIES = [
  { value: "sap", label: "SAP" },
  { value: "workday", label: "Workday" },
  { value: "ai", label: "AI" },
  { value: "microsoft", label: "Microsoft" },
  { value: "corporate-upskilling", label: "Corporate Upskilling" },
  { value: "custom", label: "Custom Training Requirement" },
];

const INDIVIDUAL_TIMELINES = ["Immediately", "Within 1 week", "Within 2 weeks", "Within 1 month", "Just exploring"];
const CORPORATE_TIMELINES = ["Immediately", "Within 1 week", "Within 2-4 weeks", "This quarter", "Need consultation first"];

const EMPTY_FORM = {
  name: "", email: "", phone: "",
  persona: "individual",
  courseCategory: "", courseName: "", courseFieldLabel: "Course Name", timeline: "",
  companyName: "", teamSize: "", preferredTimeline: "",
  notes: "", preferredDate: "", preferredTime: "",
};

/**
 * The booking form, opened directly once a lead form succeeds (see
 * BookCallFlow.jsx). Persona (Individual vs Corporate) -- and so which
 * field set shows -- is fixed from whichever form the visitor just
 * submitted; there's no toggle to switch it here. "Skip" dismisses without
 * booking. Preferred Time becomes a dependent dropdown of real open slots
 * (GET /api/public/booking/availability) once a date is picked, for
 * whichever persona's Google Calendar is configured server-side; it falls
 * back to a plain time input when that persona isn't configured yet (e.g.
 * corporate, until the ELS mailbox is finalized) -- see
 * services/google_calendar.py for the slot computation and
 * routes/booking.py for the full field mapping. For corporate, company
 * name/team size/preferred timeline also prefill from the originating
 * enquiry form (see LeadForm.jsx's onSuccess payload); Team Size's
 * <select> injects the prefilled value as an extra option when it
 * doesn't match this form's own fixed list, so a value from the enquiry
 * form's different vocabulary still shows correctly instead of going
 * blank.
 *
 * Layout: a flat left box shows everything already known and prefilled
 * from the originating lead as plain read-only text -- name/email/phone/
 * course name for individual; name/email/phone/company name/training
 * category/preferred timeline for corporate (LeadForm.jsx's onSuccess
 * payload falls back to whatever the visitor actually picked for
 * Training Category when a page doesn't lock one via
 * fixedTrainingCategory, so this is never blank in practice). There's
 * nothing to edit here, so the visual weight goes to the right column's
 * highlighted card, which holds what genuinely still needs fresh input:
 * individual's "how soon" timeline (not prefilled) alongside Preferred
 * Date; corporate's Team Size and Course Name/Requirement (not reliably
 * prefilled, and Course Name depends on Training Category for its
 * catalog-filtered dropdown); then Preferred Time and Notes for both.
 * There's no separate Country field here -- it's already captured on the
 * originating lead record.
 *
 * Preferred Date and Preferred Time use DatePicker.jsx/TimeSlotPicker.jsx
 * -- custom popovers standing in for, respectively, <input type="date">'s
 * native calendar and the plain <select> of real slots -- same
 * value/onChange contract as what they replace, so nothing about what
 * gets stored or submitted changes. The manual <input type="time">
 * fallback (persona's calendar not configured) is untouched.
 */
export default function BookingModal({ open, prefill, onClose }) {
  const rid = useId();
  const fid = (n) => `bm-${n}-${rid}`;
  const [data, setData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [meetLink, setMeetLink] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsConfigured, setSlotsConfigured] = useState(null); // null = unknown yet, true/false once checked
  const [slotsLoading, setSlotsLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setData({
      ...EMPTY_FORM,
      name: prefill?.name || "",
      email: prefill?.email || "",
      phone: prefill?.phone || "",
      persona: prefill?.persona === "corporate" ? "corporate" : "individual",
      courseName: prefill?.courseName || "",
      courseFieldLabel: prefill?.courseFieldLabel || "Course Name",
      courseCategory: prefill?.courseCategory || "",
      companyName: prefill?.companyName || "",
      teamSize: prefill?.teamSize || "",
      preferredTimeline: prefill?.preferredTimeline || "",
    });
    setErrors({});
    setSubmitting(false);
    setDone(false);
    setMeetLink(null);
  }, [open, prefill]);

  useEffect(() => {
    if (!open) return undefined;
    // No Escape-to-close here -- same reason the backdrop below has no
    // onClick: Skip (or finishing the booking) are the only intended
    // ways out, so an accidental key press can't lose what's filled in.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  const isCorporate = data.persona === "corporate";
  const timelineOptions = isCorporate ? CORPORATE_TIMELINES : INDIVIDUAL_TIMELINES;

  useEffect(() => {
    if (!open || !data.preferredDate) {
      setSlots([]);
      setSlotsConfigured(null);
      return undefined;
    }
    let cancelled = false;
    setSlotsLoading(true);
    fetch(
      `${BACKEND}/api/public/booking/availability?persona=${encodeURIComponent(data.persona)}&date=${encodeURIComponent(data.preferredDate)}`,
    )
      .then((res) => (res.ok ? res.json() : { configured: false, slots: [] }))
      .then((body) => {
        if (cancelled) return;
        setSlotsConfigured(Boolean(body?.configured));
        setSlots(Array.isArray(body?.slots) ? body.slots : []);
      })
      .catch(() => {
        if (cancelled) return;
        setSlotsConfigured(false);
        setSlots([]);
      })
      .finally(() => {
        if (!cancelled) setSlotsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, data.preferredDate, data.persona]);

  if (!open) return null;

  const onChange = (k) => (e) => {
    const value = e && e.target ? e.target.value : e;
    setData((d) => ({ ...d, [k]: value }));
    setErrors((prev) => (prev[k] ? { ...prev, [k]: undefined } : prev));
  };

  const onDateChange = (value) => {
    setData((d) => ({ ...d, preferredDate: value, preferredTime: "" }));
    setErrors((prev) => ({ ...prev, preferredDate: undefined, preferredTime: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!data.name.trim()) errs.name = "This field is required";
    if (!data.email.trim()) {
      errs.email = "This field is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) {
      errs.email = "Invalid email";
    }
    if (!data.phone) {
      errs.phone = "This field is required";
    } else if (!isValidPhoneNumber(data.phone)) {
      errs.phone = "Invalid phone number";
    }
    if (!data.preferredDate) errs.preferredDate = "This field is required";
    if (!data.preferredTime) errs.preferredTime = "This field is required";

    if (isCorporate) {
      if (!data.companyName.trim()) errs.companyName = "This field is required";
      if (!data.teamSize) errs.teamSize = "This field is required";
      if (!data.preferredTimeline) errs.preferredTimeline = "This field is required";
      if (!data.courseCategory) errs.courseCategory = "This field is required";
      // Course Name / Requirement has no UI of its own anymore -- it's
      // the same value as Training Category for corporate (see
      // LeadForm.jsx's prefill), so there's nothing left to validate here.
    } else {
      if (!data.courseName.trim()) errs.courseName = "This field is required";
      if (!data.timeline) errs.timeline = "This field is required";
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || done) return;
    if (!validate()) return;
    setSubmitting(true);

    const categoryLabel = isCorporate
      ? CORPORATE_CATEGORIES.find((c) => c.value === data.courseCategory)?.label || data.courseCategory
      : undefined;
    const payload = {
      lead_id: prefill?.leadId || undefined,
      name: data.name.trim(),
      email: data.email.trim(),
      phone: data.phone,
      persona: data.persona,
      course_category: categoryLabel,
      course_name: data.courseName.trim(),
      timeline: isCorporate ? undefined : data.timeline,
      company_name: isCorporate ? data.companyName.trim() : undefined,
      team_size: isCorporate ? data.teamSize : undefined,
      preferred_timeline: isCorporate ? data.preferredTimeline : undefined,
      notes: data.notes.trim() || undefined,
      preferred_date: data.preferredDate,
      preferred_time: data.preferredTime,
      source_page_url: prefill?.sourcePageUrl || (typeof window !== "undefined" ? window.location.href : ""),
      utm_source: prefill?.utmSource || undefined,
      utm_medium: prefill?.utmMedium || undefined,
      utm_campaign: prefill?.utmCampaign || undefined,
    };

    try {
      const res = await fetch(`${BACKEND}/api/public/booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `HTTP ${res.status}`);
      }
      const body = await res.json().catch(() => ({}));
      setMeetLink(body?.google_meet_link || null);
      setDone(true);
    } catch (err) {
      toast.error("Could not book your call", { description: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  const fieldBase =
    "w-full rounded-md px-3.5 py-2.5 text-sm bg-white border border-line text-ink placeholder:text-ink-soft/70 focus:outline-none focus:ring-2 focus:ring-royal/30 focus:border-royal";
  const labelBase = "block text-[12px] font-semibold mb-1.5 text-ink";
  const errCls = "mt-1 text-[11px] text-red-500";
  const req = <span aria-hidden className="ml-0.5 text-red-500">*</span>;
  // Plain read-only display for the always-prefilled fields in the left
  // box (name/email/phone/course name) -- no border/background, so they
  // read as settled information rather than something to edit.
  const summaryLabelCls = "text-[10px] font-semibold uppercase tracking-wider text-ink-soft/60";
  const summaryValueCls = "mt-1 text-[14.5px] font-semibold text-ink break-words";

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Book a call with a training advisor"
      data-testid={BOOKING_MODAL.root}
    >
      {/* No onClick here -- clicking outside must not dismiss the modal;
          only the Skip button or finishing a booking should. */}
      <div className="absolute inset-0 bg-ink/60 backdrop-blur-sm" aria-hidden />
      <div className="relative w-full max-w-3xl rounded-2xl border border-line bg-white shadow-[0_40px_90px_-40px_rgba(15,42,115,0.55)] max-h-[90vh] overflow-y-auto">
        <button
          type="button"
          onClick={onClose}
          data-testid={BOOKING_MODAL.skip}
          className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-semibold text-ink-soft hover:bg-bg-alt hover:text-red-500 z-10"
          aria-label="Skip booking a call"
        >
          Skip <X size={14} />
        </button>

        {done ? (
          <div data-testid={BOOKING_MODAL.success} className="p-7 text-center">
            <div className="mx-auto h-12 w-12 rounded-full bg-[var(--cyan-tint)] text-[var(--royal-deep)] flex items-center justify-center">
              <CheckCircle2 size={26} />
            </div>
            <h4 className="mt-4 font-display text-lg font-semibold text-ink">Your call is booked</h4>
            <p className="mt-1.5 text-sm text-ink-soft">
              A training advisor will reach out around {data.preferredDate} at {data.preferredTime}.
            </p>
            {meetLink && (
              <p className="mt-2 text-sm">
                <a
                  href={meetLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-royal underline underline-offset-2 hover:text-royal-deep"
                >
                  Join the Google Meet call
                </a>
              </p>
            )}
            <button
              type="button"
              onClick={onClose}
              className="mt-5 inline-flex items-center gap-2 rounded-md bg-royal-deep px-4 py-2.5 text-sm font-semibold text-white hover:bg-royal"
            >
              Close
            </button>
          </div>
        ) : (
          <div className="p-6 lg:p-7">
            <p className="font-mono-ui text-[10.5px] uppercase tracking-[0.2em] text-royal">
              {isCorporate ? "Corporate Training" : "Individual Training"}
            </p>
            <h3 className="font-display text-xl font-bold text-ink leading-tight pr-8 mt-1">
              Book a Call with a Training Advisor
            </h3>

            <form onSubmit={handleSubmit} className="mt-4" noValidate>
              <div className="grid md:grid-cols-[1fr_2fr] gap-5">
                {/* LEFT: everything already known from the enquiry form --
                    plain read-only text, not editable inputs, since
                    there's nothing to correct here. Deliberately flat (no
                    card/border) so it recedes next to the right column's
                    highlighted, still-to-fill-in fields. */}
                <div className="p-1 space-y-3">
                  <div className="divide-y divide-line/70">
                    <div className="py-2.5 first:pt-0">
                      <p className={summaryLabelCls}>Full Name</p>
                      <p className={summaryValueCls} data-testid={BOOKING_MODAL.name}>{data.name}</p>
                    </div>

                    <div className="py-2.5">
                      <p className={summaryLabelCls}>{isCorporate ? "Work Email" : "Email Address"}</p>
                      <p className={summaryValueCls} data-testid={BOOKING_MODAL.email}>{data.email}</p>
                    </div>

                    <div className="py-2.5">
                      <p className={summaryLabelCls}>Phone / WhatsApp</p>
                      <p className={summaryValueCls} data-testid={BOOKING_MODAL.phone}>
                        {formatPhoneNumberIntl(data.phone) || data.phone}
                      </p>
                    </div>

                    {isCorporate ? (
                      <>
                        <div className="py-2.5">
                          <p className={summaryLabelCls}>Company Name</p>
                          <p className={summaryValueCls} data-testid={BOOKING_MODAL.companyName}>{data.companyName}</p>
                          {errors.companyName && <p className={errCls}>{errors.companyName}</p>}
                        </div>
                        <div className="py-2.5">
                          <p className={summaryLabelCls}>Training Category</p>
                          <p className={summaryValueCls} data-testid={BOOKING_MODAL.courseCategory}>{data.courseCategory}</p>
                          {errors.courseCategory && <p className={errCls}>{errors.courseCategory}</p>}
                        </div>
                        <div className="py-2.5">
                          <p className={summaryLabelCls}>Preferred Timeline</p>
                          <p className={summaryValueCls} data-testid={BOOKING_MODAL.preferredTimeline}>{data.preferredTimeline}</p>
                          {errors.preferredTimeline && <p className={errCls}>{errors.preferredTimeline}</p>}
                        </div>
                        <div className="py-2.5 last:pb-0">
                          <p className={summaryLabelCls}>Team Size</p>
                          <p className={summaryValueCls} data-testid={BOOKING_MODAL.teamSize}>{data.teamSize}</p>
                          {errors.teamSize && <p className={errCls}>{errors.teamSize}</p>}
                        </div>
                      </>
                    ) : (
                      <div className="py-2.5 last:pb-0">
                        <p className={summaryLabelCls}>{data.courseFieldLabel}</p>
                        <p className={summaryValueCls} data-testid={BOOKING_MODAL.courseName}>{data.courseName}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* RIGHT: still needs fresh input for this call --
                    highlighted card so this is where the eye (and the
                    user's attention) goes, not the left box's settled
                    read-only info. */}
                <div className="rounded-xl border border-royal/25 bg-[var(--cyan-tint)]/50 p-4 space-y-3">
                  {!isCorporate ? (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelBase} htmlFor={fid("timeline")}>How soon are you looking to start?{req}</label>
                        <Dropdown
                          id={fid("timeline")}
                          testId={BOOKING_MODAL.timeline}
                          value={data.timeline}
                          onChange={onChange("timeline")}
                          options={timelineOptions}
                          placeholder="Select timeline"
                          hasError={Boolean(errors.timeline)}
                        />
                        {errors.timeline && <p className={errCls}>{errors.timeline}</p>}
                      </div>
                      <div>
                        <label className={labelBase} htmlFor={fid("date")}>Preferred Date{req}</label>
                        <DatePicker
                          id={fid("date")}
                          testId={BOOKING_MODAL.date}
                          value={data.preferredDate}
                          onChange={onDateChange}
                          min={new Date().toISOString().slice(0, 10)}
                          hasError={Boolean(errors.preferredDate)}
                        />
                        {errors.preferredDate && <p className={errCls}>{errors.preferredDate}</p>}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className={labelBase} htmlFor={fid("date")}>Preferred Date{req}</label>
                      <DatePicker
                        id={fid("date")}
                        testId={BOOKING_MODAL.date}
                        value={data.preferredDate}
                        onChange={onDateChange}
                        min={new Date().toISOString().slice(0, 10)}
                        hasError={Boolean(errors.preferredDate)}
                      />
                      {errors.preferredDate && <p className={errCls}>{errors.preferredDate}</p>}
                    </div>
                  )}

                  <div>
                    <label className={labelBase} htmlFor={fid("time")}>Preferred Time{req}</label>
                    {!data.preferredDate ? (
                      <div
                        id={fid("time")}
                        data-testid={BOOKING_MODAL.time}
                        aria-disabled="true"
                        className={`${fieldBase} flex items-center gap-2 text-ink-soft/70 opacity-80 cursor-not-allowed`}
                      >
                        <Clock aria-hidden size={16} className="text-royal-deep flex-shrink-0" />
                        <span className="truncate">Please select a date first to see available time slots</span>
                      </div>
                    ) : slotsConfigured ? (
                      <TimeSlotPicker
                        id={fid("time")}
                        testId={BOOKING_MODAL.time}
                        value={data.preferredTime}
                        onChange={onChange("preferredTime")}
                        slots={slots}
                        loading={slotsLoading}
                        hasError={Boolean(errors.preferredTime)}
                      />
                    ) : (
                      <input
                        id={fid("time")}
                        data-testid={BOOKING_MODAL.time}
                        type="time"
                        value={data.preferredTime}
                        onChange={onChange("preferredTime")}
                        className={fieldBase}
                        aria-invalid={Boolean(errors.preferredTime)}
                      />
                    )}
                    {errors.preferredTime && <p className={errCls}>{errors.preferredTime}</p>}
                  </div>

                  <div>
                    <label className={labelBase} htmlFor={fid("notes")}>
                      {isCorporate ? "Message / Requirement Details" : "Additional Notes"}
                    </label>
                    <textarea
                      id={fid("notes")}
                      data-testid={BOOKING_MODAL.notes}
                      value={data.notes}
                      onChange={onChange("notes")}
                      rows={isCorporate ? 3 : 4}
                      placeholder="Anything else that would help the advisor prepare"
                      className={fieldBase}
                    />
                  </div>

                  {isCorporate && (
                    <>
                      <p className="flex items-start justify-center gap-1.5 text-center text-[10.5px] leading-snug text-ink-soft">
                        <Mail aria-hidden size={11} className="mt-px flex-shrink-0" />
                        You&apos;ll get an email invite with the meeting link for your chosen date and time.
                      </p>
                      <button
                        type="submit"
                        data-testid={BOOKING_MODAL.submit}
                        disabled={submitting}
                        className={`w-full inline-flex items-center justify-center gap-2 rounded-md bg-royal-deep px-5 py-3 text-sm font-semibold text-white hover:bg-royal transition-all ${submitting ? "opacity-70 cursor-not-allowed" : ""}`}
                      >
                        {submitting ? (
                          <><Loader2 size={16} className="animate-spin" /> Booking…</>
                        ) : (
                          <>Book a Call <ArrowRight size={14} /></>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {!isCorporate && (
                <>
                  <p className="mt-4 flex items-start justify-center gap-1.5 text-center text-[10.5px] leading-snug text-ink-soft">
                    <Mail aria-hidden size={11} className="mt-px flex-shrink-0" />
                    You&apos;ll get an email invite with the meeting link for your chosen date and time.
                  </p>

                  <button
                    type="submit"
                    data-testid={BOOKING_MODAL.submit}
                    disabled={submitting}
                    className={`mt-2.5 w-full inline-flex items-center justify-center gap-2 rounded-md bg-royal-deep px-5 py-3 text-sm font-semibold text-white hover:bg-royal transition-all ${submitting ? "opacity-70 cursor-not-allowed" : ""}`}
                  >
                    {submitting ? (
                      <><Loader2 size={16} className="animate-spin" /> Booking…</>
                    ) : (
                      <>Book a Call <ArrowRight size={14} /></>
                    )}
                  </button>
                </>
              )}
            </form>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
